import { useMemo, useState } from 'react';
import type { TCEmployeeStepExecutionListResponse } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { redactSecrets } from 'src/modules/shared/utils/json/redact.util';
import {
  stringifyPreview,
  stringifySafe,
} from 'src/modules/shared/utils/json/stringify-safe.util';

interface ExecutionTechnicalDataProps {
  /** Raw execution steps returned by the API. */
  steps: TCEmployeeStepExecutionListResponse;
}

interface TechnicalStepCardProps {
  /** Raw step rendered by this card. */
  step: TCEmployeeStepExecutionListResponse[number];
  /** User-facing title for the step. */
  title: string;
}

const TECHNICAL_PAYLOAD_PREVIEW_MAX_CHARACTERS = 32_000;

const getStepTitle = (step: unknown, fallback: string): string => {
  if (typeof step !== 'object' || step === null) {
    return fallback;
  }

  if ('phase' in step && typeof step.phase === 'string') {
    return step.phase;
  }

  if ('toolName' in step && typeof step.toolName === 'string') {
    return step.toolName;
  }

  if ('workflowId' in step && typeof step.workflowId === 'string') {
    return step.workflowId;
  }

  return fallback;
};

const TechnicalStepCard: React.FC<TechnicalStepCardProps> = ({
  step,
  title,
}) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullPayload, setShowFullPayload] = useState(false);
  const payload = useMemo(() => {
    if (!isExpanded) return null;

    const redactedStep = redactSecrets(step);
    if (showFullPayload) {
      return {
        content: stringifySafe(redactedStep),
        isTruncated: false,
      };
    }

    const { preview, isTruncated } = stringifyPreview(
      redactedStep,
      TECHNICAL_PAYLOAD_PREVIEW_MAX_CHARACTERS
    );
    return { content: preview, isTruncated };
  }, [isExpanded, showFullPayload, step]);

  const handleExpandChange = (nextExpanded: boolean) => {
    setIsExpanded(nextExpanded);
    if (!nextExpanded) {
      setShowFullPayload(false);
    }
  };

  return (
    <Card
      variant="secondary"
      appearance="outline"
      shadow="none"
      className="overflow-hidden"
    >
      <ExpandableSection
        title={title}
        expanded={isExpanded}
        onExpandChange={handleExpandChange}
        isContentScrollable={false}
        showScrollHint={false}
        contentClassName="max-h-none"
      >
        {payload ? (
          <>
            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-slate-950 p-3 font-mono text-xs leading-relaxed text-slate-100">
              {payload.content}
            </pre>
            {payload.isTruncated ? (
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Text size="tiny" className="text-slate-500">
                  {t('automations.aiAgents.detail.technicalPreviewTruncated')}
                </Text>
                <Button
                  type="button"
                  variant="neutral"
                  appearance="outline"
                  size="xs"
                  onClick={() => setShowFullPayload(true)}
                >
                  {t('automations.aiAgents.detail.loadFullTechnicalPayload')}
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </ExpandableSection>
    </Card>
  );
};

/** Complete redacted execution steps for permitted support users. */
export const ExecutionTechnicalData: React.FC<ExecutionTechnicalDataProps> = ({
  steps,
}) => {
  const { t } = useI18n();

  if (steps.length === 0) {
    return (
      <Text size="small" className="text-slate-500">
        {t('automations.aiAgents.detail.noTechnicalData')}
      </Text>
    );
  }

  return (
    <div className="space-y-3">
      <Text size="small" className="text-slate-600">
        {t('automations.aiAgents.detail.technicalDescription')}
      </Text>
      {steps.map((step, index) => (
        <TechnicalStepCard
          key={`${step.executionId}-${index}`}
          step={step}
          title={getStepTitle(
            step,
            t('automations.aiAgents.detail.step', { number: index + 1 })
          )}
        />
      ))}
    </div>
  );
};
